/**
 * RightyActionHandler
 *
 * This service interprets and executes action instructions from Righty AI.
 * Actions are JSON schemas that describe what to create or modify, and this
 * handler translates them into calls to existing stores (segmentGroupStore,
 * flowStore, etc.) to perform the actual operations.
 *
 * Architecture: Frontend-first execution
 * - Righty (backend) generates action instructions as JSON
 * - This handler interprets and executes them using existing stores
 * - No duplicated CRUD logic on backend
 * - Consistent behavior with manual UI actions
 */

import { useSegmentGroupStore } from '@/modules/segment/stores/segmentGroupStore'
import { useQuestionStore } from '@/modules/segment/stores/questionStore'
import { useFlowStore } from '@/modules/flow/stores/flowStore'
import { useOfferStore } from '@/modules/offer/stores/offerStore'
import { funnelDefinitionEach } from '@/modules/flow/services/funnelDefinitions'
import { makeId, idPrefixes, uuid } from '@/services/uuid'
import type { RightyAction } from '../types'

/**
 * Result of executing an action
 */
export interface ActionResult {
  success: boolean
  createdIds: Record<string, string>
  error?: string
}

/**
 * Individual operation within a batch
 */
export interface ActionOperation {
  type: string
  data: Record<string, any>
}

/**
 * Summarized description of an action for preview
 */
export interface ActionSummary {
  type: string
  title: string
  description: string
  items?: ActionSummaryItem[]
}

export interface ActionSummaryItem {
  label: string
  value: string
}

/**
 * Execute a Righty action using existing stores
 */
export async function executeAction(action: RightyAction): Promise<ActionResult> {
  try {
    switch (action.type) {
      case 'create_dimension':
      case 'add_dimension':
        return executeCreateDimension(action.data)

      case 'create_question':
      case 'add_question':
        return executeCreateQuestion(action.data)

      case 'add_node_to_flow':
        return executeAddNodeToFlow(action.data)

      case 'batch_operations':
        return executeBatchOperations(action.data.operations || [])

      default:
        return {
          success: false,
          createdIds: {},
          error: `Unknown action type: ${action.type}`
        }
    }
  } catch (err: any) {
    return {
      success: false,
      createdIds: {},
      error: err.message || 'An unexpected error occurred'
    }
  }
}

/**
 * Create a dimension (segment group) without a question
 */
function executeCreateDimension(data: {
  name: string
  semanticType?: string
  segments?: Array<{ name: string; description?: string }>
}): ActionResult {
  const segmentGroupStore = useSegmentGroupStore()

  const segments =
    data.segments?.map((s) => ({
      id: makeId(idPrefixes.SEGMENT),
      name: s.name,
      description: s.description || ''
    })) || []

  const dimension = segmentGroupStore.create({
    type: 'segmentgroup',
    segmentGroup: {
      name: data.name,
      semanticType: data.semanticType || 'CUSTOM',
      segments
    }
  })

  return {
    success: true,
    createdIds: {
      dimension: dimension.id
    }
  }
}

/**
 * Create a question with its dimension and segments
 */
function executeCreateQuestion(data: {
  dimensionName: string
  questionText: string
  displayType?: 'buttons' | 'dropdown' | 'grid'
  semanticType?: string
  options: Array<{
    label: string
    segmentName?: string
    description?: string
  }>
}): ActionResult {
  const segmentGroupStore = useSegmentGroupStore()
  const questionStore = useQuestionStore()

  // Create segments from options
  const segments = data.options.map((opt) => ({
    id: makeId(idPrefixes.SEGMENT),
    name: opt.segmentName || opt.label,
    description: opt.description || ''
  }))

  // Create dimension with question
  const dimension = segmentGroupStore.create({
    type: 'question',
    segmentGroup: {
      name: data.dimensionName,
      semanticType: data.semanticType || 'CUSTOM',
      segments
    },
    question: {
      text: data.questionText,
      displayType: data.displayType || 'buttons'
    }
  })

  // Get the created question
  const question = questionStore.getBySegmentGroupId(dimension.id)

  return {
    success: true,
    createdIds: {
      dimension: dimension.id,
      question: question?.id || ''
    }
  }
}

/**
 * Add a node to an existing flow
 */
function executeAddNodeToFlow(data: {
  flowId: string
  nodeType: 'sg' | 'offer' | 'yesno' | 'redirect' | 'exit' | 'code' | 'data'
  position: { after?: string; before?: string; atRoot?: boolean }
  dimensionId?: string
  offerId?: string
  config?: Record<string, any>
}): ActionResult {
  const flowStore = useFlowStore()
  const segmentGroupStore = useSegmentGroupStore()

  const flow = flowStore.getById(data.flowId)
  if (!flow) {
    return {
      success: false,
      createdIds: {},
      error: `Flow not found: ${data.flowId}`
    }
  }

  // Create the new node based on type
  let newNode: any = {
    _uuid: uuid(),
    type: data.nodeType
  }

  switch (data.nodeType) {
    case 'sg': {
      // Segment group (question) node
      if (!data.dimensionId) {
        return {
          success: false,
          createdIds: {},
          error: 'dimensionId is required for sg nodes'
        }
      }
      const dimension = segmentGroupStore.getById(data.dimensionId)
      if (!dimension) {
        return {
          success: false,
          createdIds: {},
          error: `Dimension not found: ${data.dimensionId}`
        }
      }
      newNode.segmentGroupId = data.dimensionId
      newNode.ask = true
      newNode.split = dimension.segments.map((seg) => ({
        segmentId: seg.id,
        child: []
      }))
      break
    }

    case 'offer': {
      newNode.offerId = data.offerId || null
      newNode.desiredType = data.config?.desiredType || 'content'
      break
    }

    case 'yesno': {
      newNode.condition = data.config?.condition || { or: [] }
      newNode.yes = []
      newNode.no = []
      break
    }

    case 'redirect': {
      newNode.url = data.config?.url || ''
      newNode.open = data.config?.open || false
      newNode.sendSubscriberData = data.config?.sendSubscriberData || false
      break
    }

    case 'exit': {
      // Exit nodes have no additional properties
      break
    }

    case 'code': {
      newNode.code = data.config?.code || ''
      newNode.note = data.config?.note || ''
      break
    }

    case 'data': {
      newNode.actions = data.config?.actions || []
      newNode.note = data.config?.note || ''
      break
    }
  }

  // Insert the node at the specified position
  const inserted = insertNodeInFlow(flow.funnelDefinition, newNode, data.position)

  if (!inserted) {
    return {
      success: false,
      createdIds: {},
      error: 'Failed to insert node at the specified position'
    }
  }

  return {
    success: true,
    createdIds: {
      node: newNode._uuid
    }
  }
}

/**
 * Insert a node into a flow tree at the specified position
 */
function insertNodeInFlow(
  tree: any,
  newNode: any,
  position: { after?: string; before?: string; atRoot?: boolean }
): boolean {
  // Insert at root level
  if (position.atRoot) {
    if (tree.type === 'root' && Array.isArray(tree.child)) {
      tree.child.push(newNode)
      return true
    }
    return false
  }

  // Insert after a specific node
  if (position.after) {
    return insertNodeAfterUuid(tree, position.after, newNode)
  }

  // Insert before a specific node
  if (position.before) {
    return insertNodeBeforeUuid(tree, position.before, newNode)
  }

  // Default: insert at root
  if (tree.type === 'root' && Array.isArray(tree.child)) {
    tree.child.push(newNode)
    return true
  }

  return false
}

/**
 * Insert a node after a node with a specific UUID
 */
function insertNodeAfterUuid(tree: any, targetUuid: string, newNode: any): boolean {
  let inserted = false

  const insertInArray = (arr: any[]) => {
    for (let i = 0; i < arr.length; i++) {
      if (arr[i]._uuid === targetUuid) {
        arr.splice(i + 1, 0, newNode)
        inserted = true
        return true
      }
    }
    return false
  }

  // Check root child array
  if (tree.type === 'root' && Array.isArray(tree.child)) {
    if (insertInArray(tree.child)) return true
  }

  // Recursively search through tree
  funnelDefinitionEach(tree, (node: any) => {
    if (inserted) return

    // Check split branches (sg, random, highscore)
    if (Array.isArray(node.split)) {
      for (const branch of node.split) {
        if (Array.isArray(branch.child)) {
          if (insertInArray(branch.child)) return
        }
      }
    }

    // Check yesno branches
    if (Array.isArray(node.yes)) {
      if (insertInArray(node.yes)) return
    }
    if (Array.isArray(node.no)) {
      if (insertInArray(node.no)) return
    }
  })

  return inserted
}

/**
 * Insert a node before a node with a specific UUID
 */
function insertNodeBeforeUuid(tree: any, targetUuid: string, newNode: any): boolean {
  let inserted = false

  const insertInArray = (arr: any[]) => {
    for (let i = 0; i < arr.length; i++) {
      if (arr[i]._uuid === targetUuid) {
        arr.splice(i, 0, newNode)
        inserted = true
        return true
      }
    }
    return false
  }

  // Check root child array
  if (tree.type === 'root' && Array.isArray(tree.child)) {
    if (insertInArray(tree.child)) return true
  }

  // Recursively search through tree
  funnelDefinitionEach(tree, (node: any) => {
    if (inserted) return

    // Check split branches
    if (Array.isArray(node.split)) {
      for (const branch of node.split) {
        if (Array.isArray(branch.child)) {
          if (insertInArray(branch.child)) return
        }
      }
    }

    // Check yesno branches
    if (Array.isArray(node.yes)) {
      if (insertInArray(node.yes)) return
    }
    if (Array.isArray(node.no)) {
      if (insertInArray(node.no)) return
    }
  })

  return inserted
}

/**
 * Execute multiple operations in sequence
 */
async function executeBatchOperations(
  operations: ActionOperation[]
): Promise<ActionResult> {
  const allCreatedIds: Record<string, string> = {}
  const idMappings: Record<string, string> = {} // Maps placeholder IDs to real IDs

  for (const op of operations) {
    // Replace any placeholder references with actual created IDs
    const resolvedData = resolveIdReferences(op.data, idMappings)

    const result = await executeAction({
      type: op.type as any,
      data: resolvedData
    })

    if (!result.success) {
      return {
        success: false,
        createdIds: allCreatedIds,
        error: `Failed on operation ${op.type}: ${result.error}`
      }
    }

    // Track created IDs and add to mappings
    Object.entries(result.createdIds).forEach(([key, value]) => {
      allCreatedIds[`${op.type}_${key}`] = value
      // If the operation had a reference name, map it
      if (op.data._refName) {
        idMappings[op.data._refName] = value
      }
    })
  }

  return {
    success: true,
    createdIds: allCreatedIds
  }
}

/**
 * Replace placeholder ID references with actual created IDs
 */
function resolveIdReferences(
  data: Record<string, any>,
  mappings: Record<string, string>
): Record<string, any> {
  const resolved = { ...data }

  for (const [key, value] of Object.entries(resolved)) {
    if (typeof value === 'string' && value.startsWith('$ref:')) {
      const refName = value.slice(5)
      if (mappings[refName]) {
        resolved[key] = mappings[refName]
      }
    } else if (typeof value === 'object' && value !== null) {
      resolved[key] = resolveIdReferences(value, mappings)
    }
  }

  return resolved
}

/**
 * Generate a human-readable summary of an action for preview
 */
export function summarizeAction(action: RightyAction): ActionSummary[] {
  const summaries: ActionSummary[] = []

  if (action.type === 'batch_operations' && Array.isArray(action.data.operations)) {
    // Group operations by type
    const grouped: Record<string, ActionOperation[]> = {}
    for (const op of action.data.operations) {
      if (!grouped[op.type]) grouped[op.type] = []
      grouped[op.type].push(op)
    }

    // Create summary for each group
    for (const [type, ops] of Object.entries(grouped)) {
      summaries.push(summarizeSingleAction({ type, data: { count: ops.length, operations: ops } }))
    }
  } else {
    summaries.push(summarizeSingleAction(action))
  }

  return summaries
}

/**
 * Generate summary for a single action
 */
function summarizeSingleAction(action: { type: string; data: any }): ActionSummary {
  switch (action.type) {
    case 'create_dimension':
    case 'add_dimension':
      return {
        type: action.type,
        title: `Create ${action.data.count || 1} dimension${action.data.count > 1 ? 's' : ''}`,
        description: action.data.name
          ? `"${action.data.name}" with ${action.data.segments?.length || 0} segments`
          : `${action.data.count} new dimensions`,
        items: action.data.operations?.map((op: any) => ({
          label: op.data.name,
          value: `${op.data.segments?.length || 0} segments`
        }))
      }

    case 'create_question':
    case 'add_question':
      return {
        type: action.type,
        title: `Create ${action.data.count || 1} question${action.data.count > 1 ? 's' : ''}`,
        description: action.data.questionText
          ? `"${action.data.questionText}"`
          : `${action.data.count} new questions`,
        items: action.data.operations?.map((op: any) => ({
          label: op.data.dimensionName,
          value: op.data.questionText
        }))
      }

    case 'add_node_to_flow':
      const nodeTypeLabels: Record<string, string> = {
        sg: 'question',
        offer: 'offer',
        yesno: 'condition',
        redirect: 'redirect',
        exit: 'exit',
        code: 'code',
        data: 'data'
      }
      return {
        type: action.type,
        title: `Add ${action.data.count || 1} node${action.data.count > 1 ? 's' : ''} to flow`,
        description: action.data.nodeType
          ? `Add ${nodeTypeLabels[action.data.nodeType] || action.data.nodeType} node`
          : `${action.data.count} new nodes`,
        items: action.data.operations?.map((op: any) => ({
          label: nodeTypeLabels[op.data.nodeType] || op.data.nodeType,
          value: op.data.position?.after ? 'after existing node' : 'at end of flow'
        }))
      }

    default:
      return {
        type: action.type,
        title: action.type.replace(/_/g, ' '),
        description: 'Configuration change'
      }
  }
}
